import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { getTenantToday } from '../utils/dateUtils';
import { Doughnut } from 'react-chartjs-2';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RefreshCcw, 
  Upload, 
  FileText, 
  AlertCircle, 
  CheckCircle2, 
  Activity, 
  ChevronRight, 
  Download,
  Zap,
  Shield,
  FileSearch,
  Bot
} from 'lucide-react';
import { useRole } from '../hooks/useRole';
import { canRunReconciliation, canViewSensitiveData } from '../utils/roleUtils';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import { cn } from '../lib/utils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

import { DateField } from './shared/DateField';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = 
  ({ label, value, icon, color }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

// ============== Interfaces ==============
interface DashboardData {
  totalFiles: number;
  openBreaks: number;
  totalRuns: number;
  breaksByType: Record<string, number>;
  recentFiles: any[];
  recentBreaks: any[];
}

const CustodianReconciliationScreen: React.FC = () => {
  const { userRole } = useRole();
  const { isRetail } = useTenantConfig();
  const canRun = canRunReconciliation(userRole);
  const isReadOnly = userRole === 'USER';

  // SWIFT options only for institutional tenants (not retail)
  const showSwiftOptions = !isRetail;
  
  const [activeTab, setActiveTab] = useState<'dashboard' | 'upload' | 'files' | 'breaks'>('dashboard');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [files, setFiles] = useState<any[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [breaks, setBreaks] = useState<any[]>([]);
  const [custodians, setCustodians] = useState<{code: string, name: string}[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsSaving] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedCustodian, setSelectedCustodian] = useState('');
  const [fileDate, setFileDate] = useState(getTenantToday());
  const [fileType, setFileType] = useState<'csv' | 'mt535' | 'mt940'>('csv');
  const [uploadResult, setUploadResult] = useState<{success: boolean; message: string; recordCount?: number} | null>(null);

  // Modal state
  const [resolveModal, setResolveModal] = useState<{isOpen: boolean, breakId: number | null}>({isOpen: false, breakId: null});
  const [resolveStatus, setResolveStatus] = useState('RESOLVED');
  const [resolveNotes, setResolveNotes] = useState('');

  // AI Break Resolver state
  const [aiAnalysisModal, setAiAnalysisModal] = useState(false);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState<any>(null);
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/reconciliation/dashboard');
      setDashboard(response.data);
    } catch (err: any) {
      console.error('Error fetching dashboard:', err);
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait a moment before trying again.');
      } else {
        setError('Failed to load dashboard data. Please check your connection.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchFiles = useCallback(async () => {
    try {
      const response = await axios.get('/api/reconciliation/files');
      setFiles(response.data);
    } catch (err: any) {
      console.error('Error fetching files:', err);
      if (err.response?.status === 429) {
        setError('Rate limit exceeded while fetching files.');
      }
    }
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const response = await axios.get('/api/reconciliation/runs');
      setRuns(response.data);
    } catch (err: any) {
      console.error('Error fetching runs:', err);
      if (err.response?.status === 429) {
        setError('Rate limit exceeded while fetching runs.');
      }
    }
  }, []);

  const fetchBreaks = useCallback(async (runId?: number) => {
    try {
      const url = runId 
        ? `/api/reconciliation/runs/${runId}/breaks`
        : '/api/reconciliation/breaks/open';
      const response = await axios.get(url);
      setBreaks(response.data);
    } catch (err: any) {
      console.error('Error fetching breaks:', err);
      if (err.response?.status === 429) {
        setError('Rate limit exceeded while fetching breaks.');
      }
    }
  }, []);

  const fetchCustodians = useCallback(async () => {
    try {
      const response = await axios.get('/api/reconciliation/custodians');
      setCustodians(response.data);
    } catch (error) {
      console.error('Error fetching custodians:', error);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchCustodians();
  }, [fetchDashboard, fetchCustodians]);

  useEffect(() => {
    if (activeTab === 'files') {
      fetchFiles();
      fetchRuns();
    } else if (activeTab === 'breaks') {
      fetchBreaks(selectedRunId || undefined);
    }
  }, [activeTab, selectedRunId, fetchFiles, fetchRuns, fetchBreaks]);

  const handleUpload = async () => {
    if (!selectedFile || !selectedCustodian) return;

    // For CSV, file date is required; for SWIFT, default to today
    const effectiveFileDate = fileDate || getTenantToday();
    if (fileType === 'csv' && !fileDate) {
      alert('Please select a file date for CSV upload');
      return;
    }

    setIsSaving(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('custodian', selectedCustodian);
      formData.append('fileDate', effectiveFileDate);

      const response = await axios.post('/api/reconciliation/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const detectedType = response.data.fileType || 'CSV';
        setUploadResult({
          success: true,
          message: `${detectedType} file uploaded successfully!`,
          recordCount: response.data.recordCount
        });
        setSelectedFile(null);
        fetchFiles();
        fetchDashboard();
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setUploadResult({
          success: false,
          message: `Access Denied: Your role "${user.role || 'Unknown'}" does not have permission to upload. Required: ADMIN, TRADER, PORTFOLIO_MANAGER, MIDDLE_OFFICE_ADMIN, or OPERATIONS_ANALYST.`
        });
      } else {
        console.error('Upload failed:', error);
        setUploadResult({
          success: false,
          message: error.response?.data?.error || 'Upload failed'
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRunReconciliation = async (fileId: number) => {
    setIsLoading(true);
    try {
      const response = await axios.post(`/api/reconciliation/run/${fileId}`);
      if (response.data.success) {
        fetchFiles();
        fetchRuns();
        setSelectedRunId(response.data.runId);
        setActiveTab('breaks');
      }
    } catch (error) {
      console.error('Reconciliation failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveBreak = async () => {
    if (!resolveModal.breakId) return;

    try {
      await axios.put(`/api/reconciliation/breaks/${resolveModal.breakId}/resolve`, {
        status: resolveStatus,
        notes: resolveNotes
      });

      setResolveModal({isOpen: false, breakId: null});
      setResolveNotes('');
      fetchBreaks(selectedRunId || undefined);
      fetchDashboard();
    } catch (error) {
      console.error('Failed to resolve break:', error);
    }
  };

  const handleLaunchAIAnalysis = async () => {
    setAiAnalysisLoading(true);
    setAiAnalysisError(null);
    setAiAnalysisResult(null);
    setAiAnalysisModal(true);

    try {
      const response = await axios.get('/api/ai/recon/analyze-patterns');
      setAiAnalysisResult(response.data);
    } catch (error: any) {
      console.error('AI Analysis failed:', error);
      setAiAnalysisError(error.response?.data?.message || 'Failed to analyze breaks. Please try again.');
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const breakChartData = useMemo(() => {
    if (!dashboard) return null;
    const labels = Object.keys(dashboard.breaksByType || {});
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    return {
      labels,
      datasets: [{
        data: Object.values(dashboard.breaksByType || {}),
        backgroundColor: labels.map((_, i) => colors[i % colors.length]),
        borderWidth: 0,
        hoverOffset: 4
      }]
    };
  }, [dashboard]);

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <RefreshCcw className="w-8 h-8 text-blue-500" />
                Custodian Reconciliation
              </h1>
              <p className="text-slate-500 font-medium mt-1">Automated position verification against global custodian records</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
                {['dashboard', 'upload', 'files', 'breaks'].map((tab) => (
                  <button 
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold transition-all capitalize", 
                      activeTab === tab ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                    )}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <button 
                onClick={fetchDashboard}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </button>
            </div>
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-between gap-3 text-red-400 font-bold"
            >
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
              {error.includes('Rate limit') && (
                <button 
                  onClick={fetchDashboard}
                  className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 transition-colors"
                >
                  Retry Now
                </button>
              )}
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Files Uploaded" value={String(dashboard?.totalFiles || 0)} icon={<FileText className="w-5 h-5" />} color="#3b82f6" />
            <StatCard label="Open Breaks" value={String(dashboard?.openBreaks || 0)} icon={<AlertCircle className="w-5 h-5" />} color="#ef4444" />
            <StatCard label="Total Runs" value={String(dashboard?.totalRuns || 0)} icon={<Activity className="w-5 h-5" />} color="#10b981" />
            <StatCard label="Health Score" value="98.2%" icon={<Shield className="w-5 h-5" />} color="#f59e0b" />
          </div>
        </motion.div>

        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <GlassCard className="lg:col-span-2 p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  Recent Open Breaks
                </h3>
                <button className="text-xs font-bold text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors">Resolve All</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                      <th className="pb-3 px-2">CUSIP</th>
                      <th className="pb-3 px-2">Type</th>
                      <th className="pb-3 px-2 text-right">Difference</th>
                      <th className="pb-3 px-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {dashboard?.recentBreaks?.slice(0, 6).map((b, i) => (
                      <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                        <td className="py-3 px-2 font-mono text-slate-300">{b.custodianCusip || b.omsCusip}</td>
                        <td className="py-3 px-2">
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-red-500/10 text-red-400">
                            {b.breakType.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right font-bold text-white">
                          {b.valueDiff ? `$${b.valueDiff.toLocaleString()}` : '-'}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <button 
                            onClick={() => {
                              setSelectedRunId(null);
                              setActiveTab('breaks');
                            }}
                            className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(!dashboard?.recentBreaks || dashboard.recentBreaks.length === 0) && (
                      <tr>
                        <td colSpan={4} className="py-12 text-center text-slate-500 italic">No open breaks detected</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </GlassCard>

            <div className="space-y-6">
              <GlassCard className="p-6">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <FileSearch className="w-5 h-5 text-blue-400" />
                  Break Analysis
                </h3>
                <div className="h-64 flex items-center justify-center relative">
                  {breakChartData ? (
                    <Doughnut 
                      data={breakChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        cutout: '75%'
                      }}
                    />
                  ) : (
                    <div className="text-slate-600 text-sm">Waiting for metrics...</div>
                  )}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="text-2xl font-bold text-white">{dashboard?.openBreaks || 0}</div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">Total Breaks</div>
                  </div>
                </div>
                <div className="mt-6 space-y-2">
                  {Object.entries(dashboard?.breaksByType || {}).map(([type, count], i) => (
                    <div key={type} className="flex justify-between items-center text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][i % 5] }} />
                        <span className="text-slate-400 capitalize">{type.toLowerCase().replace('_', ' ')}</span>
                      </div>
                      <span className="font-bold text-white">{count as number}</span>
                    </div>
                  ))}
                </div>
              </GlassCard>

              <GlassCard className="p-6 bg-gradient-to-br from-indigo-600/20 to-violet-600/20 border-indigo-500/30 group cursor-pointer">
                <div className="flex justify-between items-start mb-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                    <Bot className="w-6 h-6" />
                  </div>
                  <Zap className="w-4 h-4 text-amber-400" />
                </div>
                <h4 className="font-bold text-white mb-1">AI Break Resolver</h4>
                <p className="text-xs text-slate-400 leading-relaxed mb-4">Let Axiom AI analyze data patterns and suggest resolutions for your breaks.</p>
                <button
                  onClick={handleLaunchAIAnalysis}
                  disabled={aiAnalysisLoading}
                  className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 disabled:cursor-wait text-white text-xs font-bold rounded-lg transition-all shadow-lg shadow-indigo-600/20"
                >
                  {aiAnalysisLoading ? 'Analyzing...' : 'Launch Intelligence'}
                </button>
              </GlassCard>
            </div>
          </div>
        )}

        {activeTab === 'upload' && (
          <GlassCard className="p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-white mb-6">Upload Custodian Position File</h3>

            {/* File Type Selection */}
            <div className="mb-6">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-3">File Type</label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="radio"
                    name="fileType"
                    value="csv"
                    checked={fileType === 'csv'}
                    onChange={() => { setFileType('csv'); setSelectedFile(null); setUploadResult(null); }}
                    className="w-4 h-4 text-blue-600 bg-slate-950 border-white/20 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-300 group-hover:text-white transition-colors">CSV Position File</span>
                </label>
                {/* SWIFT options only for institutional tenants */}
                {showSwiftOptions && (
                  <>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="fileType"
                        value="mt535"
                        checked={fileType === 'mt535'}
                        onChange={() => { setFileType('mt535'); setSelectedFile(null); setUploadResult(null); }}
                        className="w-4 h-4 text-blue-600 bg-slate-950 border-white/20 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-300 group-hover:text-white transition-colors">SWIFT MT535 (Statement of Holdings)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="radio"
                        name="fileType"
                        value="mt940"
                        checked={fileType === 'mt940'}
                        onChange={() => { setFileType('mt940'); setSelectedFile(null); setUploadResult(null); }}
                        className="w-4 h-4 text-blue-600 bg-slate-950 border-white/20 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-300 group-hover:text-white transition-colors">SWIFT MT940 (Bank Statement)</span>
                    </label>
                  </>
                )}
              </div>
            </div>

            <div
              className={cn(
                "border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer",
                isDragging ? "border-blue-500 bg-blue-500/10" : "border-white/10 hover:border-blue-500/50 hover:bg-white/5"
              )}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files[0];
                if (file) {
                  setSelectedFile(file);
                  setUploadResult(null);
                  // Auto-detect file type from filename
                  const fileName = file.name.toLowerCase();
                  if (fileName.includes('mt535') || fileName.includes('holdings')) {
                    setFileType('mt535');
                  } else if (fileName.includes('mt940') || fileName.includes('statement')) {
                    setFileType('mt940');
                  } else if (fileName.endsWith('.csv')) {
                    setFileType('csv');
                  }
                }
              }}
              onClick={() => document.getElementById('fileInput')?.click()}
            >
              <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-300 font-medium">
                {selectedFile ? selectedFile.name :
                  fileType === 'csv' ? "Drag and drop a CSV file, or click to browse" :
                  fileType === 'mt535' ? "Drag and drop a SWIFT MT535 file (.txt, .swift)" :
                  "Drag and drop a SWIFT MT940 file (.txt, .swift)"}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                {fileType === 'csv' ? 'Accepts: .csv files' : 'Accepts: .txt, .swift, .fin, .dat files'}
              </p>
              <input
                id="fileInput"
                type="file"
                className="hidden"
                accept={fileType === 'csv' ? '.csv' : '.txt,.swift,.fin,.dat,.mt535,.mt940'}
                onChange={(e) => {
                  setSelectedFile(e.target.files?.[0] || null);
                  setUploadResult(null);
                }}
              />
            </div>

            {/* Upload Result Message */}
            {uploadResult && (
              <div className={cn(
                "mt-4 p-4 rounded-xl flex items-center gap-3",
                uploadResult.success
                  ? "bg-emerald-500/10 border border-emerald-500/20"
                  : "bg-red-500/10 border border-red-500/20"
              )}>
                {uploadResult.success ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                )}
                <div>
                  <p className={cn("font-medium", uploadResult.success ? "text-emerald-400" : "text-red-400")}>
                    {uploadResult.message}
                  </p>
                  {uploadResult.recordCount !== undefined && (
                    <p className="text-sm text-slate-400 mt-1">{uploadResult.recordCount} positions parsed</p>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Custodian</label>
                <select
                  value={selectedCustodian}
                  onChange={(e) => setSelectedCustodian(e.target.value)}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/50 appearance-none"
                >
                  <option value="">Select Custodian...</option>
                  {custodians.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              {fileType === 'csv' && (
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Position Date</label>
                  <DateField
                    value={fileDate ?? ''}
                    className="w-full"
                    onChange={iso => setFileDate(iso)}
                  />
                </div>
              )}
            </div>

            <button
              onClick={handleUpload}
              disabled={isUploading || !selectedFile || !selectedCustodian || (fileType === 'csv' && !fileDate)}
              className="w-full mt-8 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2"
            >
              {isUploading ? <RefreshCcw className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              {fileType === 'csv' ? 'Upload & Parse CSV' :
               fileType === 'mt535' ? 'Upload & Parse MT535 Holdings' :
               'Upload & Parse MT940 Statement'}
            </button>
          </GlassCard>
        )}

        {activeTab === 'files' && (
          <div className="space-y-6">
            <GlassCard className="p-6">
              <h3 className="text-lg font-bold text-white mb-6">Recent Uploads</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                      <th className="pb-3 px-2">ID</th>
                      <th className="pb-3 px-2">File Name</th>
                      <th className="pb-3 px-2">Type</th>
                      <th className="pb-3 px-2">Custodian</th>
                      <th className="pb-3 px-2">Date</th>
                      <th className="pb-3 px-2">Records</th>
                      <th className="pb-3 px-2">Status</th>
                      <th className="pb-3 px-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {files.map(f => (
                      <tr key={f.id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-2 text-slate-400">#{f.id}</td>
                        <td className="py-4 px-2 font-medium text-white">{f.fileName}</td>
                        <td className="py-4 px-2">
                          <span className={cn(
                            "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase",
                            f.fileType === 'MT535' ? "bg-indigo-500/10 text-indigo-400" :
                            f.fileType === 'MT940' ? "bg-amber-500/10 text-amber-400" :
                            "bg-slate-500/10 text-slate-400"
                          )}>
                            {f.fileType || 'CSV'}
                          </span>
                        </td>
                        <td className="py-4 px-2 text-slate-300">{f.custodian}</td>
                        <td className="py-4 px-2 text-slate-300">{f.fileDate}</td>
                        <td className="py-4 px-2 text-slate-300">{f.recordCount}</td>
                        <td className="py-4 px-2">
                          <span className={cn(
                            "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase",
                            f.status === 'UPLOADED' ? "bg-blue-500/10 text-blue-400" : "bg-emerald-500/10 text-emerald-400"
                          )}>
                            {f.status}
                          </span>
                        </td>
                        <td className="py-4 px-2 text-right">
                          {f.status === 'UPLOADED' && (
                            <button
                              onClick={() => handleRunReconciliation(f.id)}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-all"
                            >
                              Run Reconciliation
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GlassCard>

            <GlassCard className="p-6">
              <h3 className="text-lg font-bold text-white mb-6">Reconciliation Runs</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                      <th className="pb-3 px-2">Run ID</th>
                      <th className="pb-3 px-2">Time</th>
                      <th className="pb-3 px-2">Custodian Pos</th>
                      <th className="pb-3 px-2">OMS Pos</th>
                      <th className="pb-3 px-2">Matched</th>
                      <th className="pb-3 px-2">Breaks</th>
                      <th className="pb-3 px-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {runs.map(r => (
                      <tr key={r.id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-2 text-slate-400">#{r.id}</td>
                        <td className="py-4 px-2 text-slate-300">{new Date(r.runTime).toLocaleString()}</td>
                        <td className="py-4 px-2 text-slate-300">{r.totalCustodianPositions}</td>
                        <td className="py-4 px-2 text-slate-300">{r.totalOmsPositions}</td>
                        <td className="py-4 px-2 text-emerald-400 font-bold">{r.matchedCount}</td>
                        <td className="py-4 px-2 text-red-400 font-bold">{r.breakCount}</td>
                        <td className="py-4 px-2 text-right">
                          <button 
                            onClick={() => {
                              setSelectedRunId(r.id);
                              setActiveTab('breaks');
                            }}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-lg transition-all border border-white/5"
                          >
                            View Breaks
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          </div>
        )}

        {activeTab === 'breaks' && (
          <GlassCard className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-white">
                {selectedRunId ? `Breaks for Run #${selectedRunId}` : 'All Open Breaks'}
              </h3>
              {selectedRunId && (
                <button 
                  onClick={() => { setSelectedRunId(null); fetchBreaks(); }}
                  className="text-xs font-bold text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors"
                >
                  Show All Open
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="pb-3 px-2">Type</th>
                    <th className="pb-3 px-2">CUSIP</th>
                    <th className="pb-3 px-2">Custodian Qty</th>
                    <th className="pb-3 px-2">OMS Qty</th>
                    <th className="pb-3 px-2 text-right">Difference</th>
                    <th className="pb-3 px-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {breaks.map(b => (
                    <tr key={b.id} className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                      <td className="py-4 px-2">
                        <span className={cn(
                          "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase",
                          b.breakType === 'CUSTODIAN_ONLY' ? "bg-red-500/10 text-red-400" :
                          b.breakType === 'OMS_ONLY' ? "bg-orange-500/10 text-orange-400" :
                          "bg-yellow-500/10 text-yellow-400"
                        )}>
                          {b.breakType.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-4 px-2 font-mono text-slate-300">{b.custodianCusip || b.omsCusip}</td>
                      <td className="py-4 px-2 text-slate-300">{b.custodianQuantity?.toLocaleString() || '-'}</td>
                      <td className="py-4 px-2 text-slate-300">{b.omsQuantity?.toLocaleString() || '-'}</td>
                      <td className="py-4 px-2 text-right font-bold text-white">
                        {b.valueDiff ? `$${b.valueDiff.toLocaleString()}` : b.quantityDiff ? b.quantityDiff.toLocaleString() : '-'}
                      </td>
                      <td className="py-4 px-2 text-right">
                        <button 
                          onClick={() => setResolveModal({isOpen: true, breakId: b.id})}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-all"
                        >
                          Resolve
                        </button>
                      </td>
                    </tr>
                  ))}
                  {breaks.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-500 italic">No breaks found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </GlassCard>
        )}
      </div>

      <AnimatePresence>
        {resolveModal.isOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
              onClick={() => setResolveModal({isOpen: false, breakId: null})}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-3xl shadow-2xl p-8"
            >
              <h3 className="text-xl font-bold text-white mb-6">Resolve Break #{resolveModal.breakId}</h3>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Resolution Status</label>
                  <select 
                    value={resolveStatus}
                    onChange={(e) => setResolveStatus(e.target.value)}
                    className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/50 appearance-none"
                  >
                    <option value="RESOLVED">Resolved (Positions Fixed)</option>
                    <option value="ACCEPTED">Accepted (Valid Timing Difference)</option>
                    <option value="INVESTIGATING">Still Investigating</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Resolution Notes</label>
                  <textarea 
                    value={resolveNotes}
                    onChange={(e) => setResolveNotes(e.target.value)}
                    className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 min-h-[120px]"
                    placeholder="Describe how this break was resolved..."
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button 
                    onClick={() => setResolveModal({isOpen: false, breakId: null})}
                    className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleResolveBreak}
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-600/20"
                  >
                    Save Resolution
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* AI Analysis Modal */}
      <AnimatePresence>
        {aiAnalysisModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden"
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                    <Bot className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">AI Break Analysis</h3>
                    <p className="text-xs text-slate-400">Pattern detection and resolution suggestions</p>
                  </div>
                </div>
                <button
                  onClick={() => setAiAnalysisModal(false)}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {aiAnalysisLoading && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mb-4" />
                    <p className="text-slate-400">Analyzing {dashboard?.openBreaks || 0} breaks...</p>
                    <p className="text-xs text-slate-500 mt-1">This may take a moment</p>
                  </div>
                )}

                {aiAnalysisError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                    <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                    <p className="text-red-400 font-medium">{aiAnalysisError}</p>
                  </div>
                )}

                {aiAnalysisResult && !aiAnalysisLoading && (
                  <div className="space-y-6">
                    {/* Error message if present */}
                    {aiAnalysisResult.error && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                        <p className="text-amber-400 text-sm">⚠️ AI service unavailable. Showing statistical analysis.</p>
                      </div>
                    )}

                    {/* Break Type Statistics */}
                    {aiAnalysisResult.breakTypeCounts && (
                      <div>
                        <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                          <Activity className="w-4 h-4 text-blue-400" /> Break Statistics
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {Object.entries(aiAnalysisResult.breakTypeCounts).map(([type, count]) => (
                            <div key={type} className="bg-slate-800/50 border border-white/5 rounded-lg p-4">
                              <p className="text-2xl font-bold text-white">{String(count)}</p>
                              <p className="text-xs text-slate-400 capitalize">{type.toLowerCase().replace(/_/g, ' ')}</p>
                            </div>
                          ))}
                          <div className="bg-indigo-500/20 border border-indigo-500/30 rounded-lg p-4">
                            <p className="text-2xl font-bold text-indigo-400">{aiAnalysisResult.totalBreaks}</p>
                            <p className="text-xs text-slate-400">Total Breaks</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* AI Pattern Analysis */}
                    {aiAnalysisResult.patternAnalysis && (
                      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4">
                        <h4 className="text-sm font-bold text-indigo-400 mb-3 flex items-center gap-2">
                          <Zap className="w-4 h-4" /> AI Analysis
                        </h4>
                        <div className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                          {aiAnalysisResult.patternAnalysis}
                        </div>
                      </div>
                    )}

                    {/* Summary (legacy format support) */}
                    {aiAnalysisResult.summary && !aiAnalysisResult.patternAnalysis && (
                      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4">
                        <h4 className="text-sm font-bold text-indigo-400 mb-2 flex items-center gap-2">
                          <Zap className="w-4 h-4" /> Summary
                        </h4>
                        <p className="text-slate-300 text-sm leading-relaxed">{aiAnalysisResult.summary}</p>
                      </div>
                    )}

                    {/* Patterns (legacy format support) */}
                    {aiAnalysisResult.patterns && aiAnalysisResult.patterns.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                          <Activity className="w-4 h-4 text-blue-400" /> Detected Patterns
                        </h4>
                        <div className="space-y-3">
                          {aiAnalysisResult.patterns.map((pattern: any, idx: number) => (
                            <div key={idx} className="bg-slate-800/50 border border-white/5 rounded-xl p-4">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-medium text-white">{pattern.type || pattern.name || `Pattern ${idx + 1}`}</span>
                                {pattern.count && (
                                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full">{pattern.count} occurrences</span>
                                )}
                              </div>
                              <p className="text-sm text-slate-400">{pattern.description || pattern.details}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendations (legacy format support) */}
                    {aiAnalysisResult.recommendations && aiAnalysisResult.recommendations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400" /> Recommendations
                        </h4>
                        <div className="space-y-2">
                          {aiAnalysisResult.recommendations.map((rec: any, idx: number) => (
                            <div key={idx} className="flex items-start gap-3 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                              <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</span>
                              <p className="text-sm text-slate-300">{typeof rec === 'string' ? rec : rec.action || rec.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Success indicator */}
                    {aiAnalysisResult.success && (
                      <div className="flex items-center gap-2 text-green-400 text-xs">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Analysis completed successfully</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-white/10 flex justify-end gap-3">
                <button
                  onClick={() => setAiAnalysisModal(false)}
                  className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
                {aiAnalysisResult && (
                  <button
                    onClick={handleLaunchAIAnalysis}
                    className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Re-analyze
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default CustodianReconciliationScreen;
